import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { useScrollContext } from "@navigations/utils/ScrollContext";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AppState,
  DeviceEventEmitter,
  Platform,
  TouchableOpacity,
  View,
} from "react-native";
import Carousel, {
  CarouselRenderItem,
  ICarouselInstance,
} from "react-native-reanimated-carousel";
import styled, { css, useTheme } from "styled-components/native";
import GalleryViewModal from "../GalleryViewModel/GalleryViewModal";
import LoaderModel from "../LoaderModel/LoaderModel";
import ShareModal from "../Share";
import { ShareType } from "../Share/types";
import { useSelector } from "react-redux";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { GalleryCategory } from "./utils/GalleryType";
import ImagePreview from "./utils/ImagePreview";
import { getShapeValue } from "./utils/EmptyImage/utils";
import { IProps, ImageObject, RenderImageProps } from "./utils/types";
import CarouselThumbs from "./utils/CarouselThumbs";
import { configureHorizontalCarouselPanGesture } from "./utils/carouselPanGesture";
import FavoriteButton from "./utils/FavoriteButton";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTranslation } from "react-i18next";
import ShareMenu from "../ShareMenu";
import ShareMenuiPadWrapper from "../ShareMenuiPadWrapper";
import { Spacer } from "@components/atoms";
import {
  isPair,
  isParcel,
  isThreeDEnabled,
} from "@utils/product/productValidators";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import { MediaType, ProductCategory } from "@utils/constants";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { PdpSections } from "@utils/customization";
import { mapKeysToLabels, ShareVia } from "../Share/utils/utils";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useThreeDProperties } from "../../../contexts/ThreeDPropertiesContext";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { ImageContentFit } from "expo-image";
import { DefaultTheme } from "styled-components/native";
import { Diamond } from "@src/types/DiamondDetail";
const ImageGallery = ({
  hideThumbs = false,
  resizeMode = "cover",
  thumbColor = "#d9b44a",
  thumbDefaultBorderColor = "#000000",
  thumbResizeMode = "cover",
  thumbSize,
  iconSize,
  testIDPrefix = "pdp.gallery",
  ...props
}: IProps) => {
  const {
    images,
    initialIndex,
    isOpen,
    renderCustomImage,
    renderFooterComponent,
    isExternalView,
    image_thumb_url,
    isSmallDeviceWidth = false,
    imageWidth,
    handleOpenExpandModel,
    applyRadius = false,
    resizeImage = false,
    productType,
    itemDetail,
    extraSmallImage,
    isClickable = false,
    scrollEnabled = true,
    addExtraPadding = false,
    isMatchModel = false,
    showExpand = false,
    showMediaModel = false,
    isSmallVideoView = false,
    GIACertEnable = false,
    setMediaActiveIndex,
    mediaActiveIndex,
    fromReviewPage = false,
    isJewelryComponent = false,
    onRemoveMedia,
    isModal = false,
    onShareViaNativeFromModal: externalShareHandler,
  } = props;

  const {
    itemDetail: shareItemDetail,
    stoneItemDetail,
    matchingPairItemDetail,
    jcSelectedOption,
    personalization,
  } = useJewelryConfiguratorState();

  const personalizationSectionLayout = useGetPdpSectionLayout(
    PdpSections.PERSONALIZATION,
    productType
  );

  const shareDiamond = fromReviewPage && isModal;

  const theme = useTheme();
  const { initStatus } = useOrgSettingsSlice();
  const [activeIndex, setActiveIndex] = useState(0);
  const topRef = useRef<ICarouselInstance>(null);
  const userInfo = useUserInfo();
  const isGuest = useSelector((state: any) => state.userInfo.isGuest);
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const three_d_settings = useSelector(
    (state: any) => state.globalData?.app_settings?.three_d
  );
  const { threeDProperties, setThreeDProperties } = useThreeDProperties();
  thumbSize = !thumbSize ? (theme.isLargeScreen ? 64 : 56) : thumbSize;

  const [scrollValue, ___, _____, ______, _______] = useScrollContext();

  const [showShareModal, setshowShareModal] = useState(false);

  const [isSelectLanguageModalOpen, setIsSelectLanguageModalOpen] =
    useState(false);

  const [loadedItems, setLoadedItems] = useState({});

  const handleModalClose = useCallback(
    (shareMediaUrl?: string) => {
      setIsSelectLanguageModalOpen(false);
      if (shareMediaUrl && externalShareHandler) {
        externalShareHandler(shareMediaUrl);
      }
    },
    [externalShareHandler]
  );

  const { deviceWidth: screenWidth } = useIsFoldable();
  const deviceWidth = isSmallDeviceWidth ? imageWidth : screenWidth;

  let isEmpty = images?.length === 0;

  const imageEmptyObject: ImageObject = {
    link: "",
    type: "image",
    video_type: "",
  };

  useEffect(() => {
    if (Platform.OS !== "android") return;
    const subscription = AppState.addEventListener("change", (state) => {
      if (state !== "inactive") {
        setIsSelectLanguageModalOpen(false);
      }
    });
    return () => subscription.remove();
  }, []);

  const getValidIndex = (index?: number) => {
    if (
      typeof index === "number" &&
      index >= 0 &&
      index < (images?.length ?? 0)
    ) {
      return index;
    }

    return 0;
  };

  useEffect(() => {
    scrollToIndex(mediaActiveIndex ?? 0);
  }, [mediaActiveIndex]);

  useEffect(() => {
    if (!images) return;

    if (images.length === 0) {
      setActiveIndex(0);
      changeSelectedIndex(0);
      return;
    }

    const nextIndex = getValidIndex(mediaActiveIndex ?? activeIndex);

    setActiveIndex(nextIndex);
    if (mediaActiveIndex !== nextIndex) {
      setMediaActiveIndex?.(nextIndex);
    }
    changeSelectedIndex(nextIndex);
  }, [itemDetail, images]);

  useEffect(() => {
    DeviceEventEmitter.addListener("RESET_GALLERY", () => {
      const imageIndex = images?.findIndex(
        (image) => image.type === MediaType.MODEL_3D
      );
      if (
        imageIndex !== undefined &&
        activeIndex !== imageIndex &&
        imageIndex !== -1
      ) {
        scrollToIndex(imageIndex);
      }
    });
  }, [images, activeIndex]);

  const scrollToIndex = (i: number) => {
    const nextIndex = getValidIndex(i);

    setActiveIndex(nextIndex);
    setMediaActiveIndex?.(nextIndex);
    if (topRef?.current) {
      topRef.current?.scrollTo({
        index: nextIndex,
        animated: true,
      });
    }
  };

  const expandViewData = () => {
    handleOpenExpandModel();
  };

  const handleItemClick = (item: any, activeCurrentIndex: number) => {
    if (isClickable) {
      setActiveIndex(activeCurrentIndex);
      setIsSelectLanguageModalOpen(!isSelectLanguageModalOpen);
    }
  };

  const renderItem = ({ item, index }: RenderImageProps) => {
    if (
      loadedItems[index as keyof typeof loadedItems] ||
      index === activeIndex
    ) {
      return (
        <ImageContainer>
          <ImagePreview
            index={index}
            isSelected={activeIndex === index}
            item={item}
            resizeMode={resizeMode as ImageContentFit}
            handleItemClick={() =>
              extraSmallImage
                ? handleOpenExpandModel
                : handleItemClick(item, index)
            }
            activeIndex={activeIndex}
            renderCustomImage={renderCustomImage}
            isSmallDeviceWidth={isSmallDeviceWidth}
            deviceWidth={deviceWidth}
            applyRadius={applyRadius}
            resizeImage={resizeImage}
            extraSmallImage={extraSmallImage}
            isExternalView={isExternalView}
            shape={getShapeValue(itemDetail)}
            isMatchModel={isMatchModel}
            showMediaModel={showMediaModel}
            isSmallVideoView={isSmallVideoView}
            itemID={itemDetail?.id as number}
            certNum={itemDetail?.cert_num}
            linkedPairChildren={itemDetail?.children}
            threeDMedia={
              shareItemDetail?.three_d_media || itemDetail?.three_d_media || []
            }
            GIACertEnable={GIACertEnable}
            stoneType={
              isPair(itemDetail)
                ? SearchFilterTypeEnum.PAIRS
                : isCustomApp && isParcel(itemDetail)
                ? SearchFilterTypeEnum.PARCEL
                : SearchFilterTypeEnum.SINGLE_STONES
            }
            onRemoveMedia={onRemoveMedia}
          />
        </ImageContainer>
      );
    }
  };

  const EmptyItem = ({ item, index }: RenderImageProps) => {
    const EmptyImageContainer =
      theme.isTablet && !isMatchModel ? View : ImageContainer;
    return (
      <EmptyImageContainer
        style={theme.isTablet ? { height: 380, width: 380 } : {}}
        screenWidth={deviceWidth}
      >
        <ImagePreview
          index={index}
          isSelected={activeIndex === index}
          item={item}
          resizeMode={resizeMode as ImageContentFit}
          handleItemClick={
            extraSmallImage ? handleOpenExpandModel : handleItemClick
          }
          activeIndex={activeIndex}
          renderCustomImage={renderCustomImage}
          isSmallDeviceWidth={isSmallDeviceWidth}
          deviceWidth={deviceWidth}
          applyRadius={applyRadius}
          resizeImage={resizeImage}
          extraSmallImage={extraSmallImage}
          shape={getShapeValue(itemDetail)}
          stoneType={
            isPair(itemDetail)
              ? SearchFilterTypeEnum.PAIRS
              : isCustomApp && isParcel(itemDetail)
              ? SearchFilterTypeEnum.PARCEL
              : SearchFilterTypeEnum.SINGLE_STONES
          }
        />
        {images?.length !== 0 && <Divider />}
      </EmptyImageContainer>
    );
  };

  const onSnapToItem = (index: number) => {
    setActiveIndex(index);
    setMediaActiveIndex?.(index);
    setLoadedItems((prevLoadedItems) => ({
      ...prevLoadedItems,
      [index]: true,
    }));
  };

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(initialIndex ? initialIndex : 0);
      changeSelectedIndex(initialIndex ? initialIndex : 0);
    }
  }, [isOpen, initialIndex]);

  const changeSelectedIndex = (i: any) => {
    topRef.current?.scrollTo({
      index: i,
      animated: false,
    });
  };

  const baseOptions = {
    vertical: false,
    layout: "default",
    width: resizeImage
      ? theme.isLargeScreen
        ? deviceWidth
        : 380
      : deviceWidth,
    height: 400,
  } as const;

  const [showLoaderModal, setLoaderModal] = useState(false);
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState("");

  const rotate3D = () => {
    if (!isThreeDEnabled(itemDetail)) return;
    DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
      iframeKey: [
        "vdb-gallery-iframe",
        "vdb-sticky-iframe",
        "vdb-overlay-iframe",
      ],
      data: {
        three_d_data_type:
          initStatus?.three_d_settings?.three_d_data_type ?? "webgi",
        functionName: "setAutoRotate",
        params: { enabled: !threeDProperties.autoRotate },
      },
    });
    setThreeDProperties((prevState: any) => ({
      ...prevState,
      autoRotate: !prevState.autoRotate,
    }));
  };

  const captureCanvas = () => {
    if (!isThreeDEnabled(itemDetail)) return;
    DeviceEventEmitter.emit("THREE_D_WEBVIEW", {
      iframeKey: ["vdb-gallery-iframe"],
      data: {
        three_d_data_type:
          initStatus?.three_d_settings?.three_d_data_type ?? "webgi",
        functionName: "captureCanvas",
        params: {},
      },
    });
  };

  if ((imageWidth ?? 0) === 0) return <View />;

  return (
    <Container>
      <MainContainer isSmallDeviceWidth={isSmallDeviceWidth}>
        <HeadContainer
          isEmpty={isEmpty}
          resizeImage={resizeImage}
          deviceWidth={deviceWidth}
        >
          <IndexContainer resizeImage={resizeImage}>
            <PdpMediaActionsContainer>
              <HeaderTextContainer>
                {showExpand && (
                  <TouchableOpacity
                    testID={`${testIDPrefix}.mediaActions.expand`}
                    onPress={() =>
                      !isMatchModel
                        ? handleItemClick(null, activeIndex)
                        : expandViewData()
                    }
                    activeOpacity={0.8}
                  >
                    <IconContainer>
                      <VDBIcon
                        name={VDBIcons.EXPAND}
                        size={24}
                        color={theme["core-06"]}
                        variant={IconsVariant.VDBCustom}
                      />
                    </IconContainer>
                  </TouchableOpacity>
                )}
                {!!isThreeDEnabled(itemDetail) &&
                  images?.[activeIndex]?.type === MediaType.MODEL_3D && (
                    <TouchableOpacity
                      testID={`${testIDPrefix}.mediaActions.threeDToggle`}
                      onPress={rotate3D}
                      activeOpacity={0.8}
                    >
                      <IconContainer>
                        <VDBIcon
                          name={
                            threeDProperties.autoRotate
                              ? VDBIcons.THREE_D_PAUSE
                              : VDBIcons.THREE_D_PLAY
                          }
                          size={24}
                          color={theme["core-06"]}
                          variant={IconsVariant.VDBCustom}
                        />
                      </IconContainer>
                    </TouchableOpacity>
                  )}
                {Platform.OS === "web" &&
                  !!isThreeDEnabled(itemDetail) &&
                  (three_d_settings?.showScreenshotIcon ?? false) &&
                  images?.[activeIndex]?.type === MediaType.MODEL_3D && (
                    <TouchableOpacity
                      testID={`${testIDPrefix}.mediaActions.screenshot`}
                      onPress={captureCanvas}
                      activeOpacity={0.8}
                    >
                      <IconContainer>
                        <VDBIcon
                          name={VDBIcons.SCREENSHOT}
                          size={24}
                          color={theme["core-06"]}
                          variant={IconsVariant.VDBCustom}
                        />
                      </IconContainer>
                    </TouchableOpacity>
                  )}
              </HeaderTextContainer>
              {(isExternalView === GalleryCategory.productDetail ||
                isExternalView === GalleryCategory.matchScreenDetail) &&
                !userInfo.isIFrame() && (
                  <HeaderTextContainer>
                    {!isJewelryComponent && (
                      <View
                        style={{ pointerEvents: "auto" }}
                        testID={`${testIDPrefix}.mediaActions.share`}
                      >
                        {theme.isTablet && Platform.OS !== "web" ? (
                          <ShareMenuiPadWrapper
                            setSelectedOption={setSelectedOption}
                            setshowShareModal={setshowShareModal}
                            addExtraPadding={addExtraPadding}
                            itemDetail={itemDetail}
                            mediaUrl={images?.[activeIndex]?.link}
                            productType={productType}
                          />
                        ) : (
                          <ShareMenu
                            setSelectedOption={setSelectedOption}
                            setshowShareModal={setshowShareModal}
                            addExtraPadding={addExtraPadding}
                            isOpen={isOpen}
                            isSmallDeviceWidth={isSmallDeviceWidth}
                            resizeImage={resizeImage}
                            showExpand={showExpand}
                            itemDetail={itemDetail}
                            mediaUrl={images?.[activeIndex]?.link}
                            useRNModal={isModal}
                            isModal={isModal}
                            productType={productType}
                          />
                        )}
                      </View>
                    )}
                    {!(fromReviewPage && !isModal) && !isJewelryComponent && (
                      <View
                        style={{ pointerEvents: "auto" }}
                        testID={`${testIDPrefix}.mediaActions.favorite`}
                      >
                        <FavoriteButton
                          productType={productType}
                          itemDetail={itemDetail}
                          scrollValue={scrollValue}
                          //@ts-ignore
                          isMatchPairScreen={isSmallDeviceWidth}
                          isOpen={isOpen}
                          showExpand={showExpand}
                          resizeImage={resizeImage}
                        />
                      </View>
                    )}
                  </HeaderTextContainer>
                )}
            </PdpMediaActionsContainer>
            {theme.isLargeScreen ? <Spacer size={SpaceType.Basic} /> : null}
            {images !== undefined && images?.length !== 0 ? (
              <NewHeadContainer>
                <Carousel
                  key="gallery-images"
                  {...baseOptions}
                  onConfigurePanGesture={configureHorizontalCarouselPanGesture}
                  data={images}
                  renderItem={renderItem as CarouselRenderItem<ImageObject>}
                  enabled={
                    scrollEnabled &&
                    images[activeIndex]?.type !== MediaType.MODEL_3D
                  }
                  maxScrollDistancePerSwipe={
                    scrollEnabled &&
                    images[activeIndex]?.type !== MediaType.MODEL_3D
                      ? undefined
                      : 0
                  }
                  snapEnabled={
                    scrollEnabled &&
                    images[activeIndex]?.type !== MediaType.MODEL_3D
                      ? false
                      : true
                  }
                  {...(theme.isLargeScreen
                    ? {
                        width: imageWidth,
                        height: resizeImage
                          ? !theme.isMobile
                            ? imageWidth
                            : 200
                          : imageWidth,
                      }
                    : theme.isMobile
                    ? {
                        width: deviceWidth,
                        height: deviceWidth,
                      }
                    : {})}
                  pagingEnabled={true}
                  onSnapToItem={onSnapToItem}
                  ref={topRef}
                  //@ts-ignore
                  enableMomentum={true}
                  decelerationRate={0.9}
                  carouselController={true}
                  loop={images?.length > 1 ? false : true}
                />
              </NewHeadContainer>
            ) : (
              <EmptyItem item={imageEmptyObject} index={0} />
            )}
          </IndexContainer>
          <HeadChildContainer>
            {hideThumbs || images?.length === 0 ? null : (
              <ThumbContainer testID={`${testIDPrefix}.thumbs.wrapper`}>
                <CarouselThumbs
                  testIDPrefix={`${testIDPrefix}.thumbs`}
                  data={images ?? []}
                  vertical={theme.isLargeScreen ? true : false}
                  itemDetail={itemDetail ?? ({} as Diamond)}
                  activeIndex={activeIndex}
                  image_thumb_url={image_thumb_url ?? ""}
                  changeActiveIndex={(i: number) => {
                    //setActiveIndex(i);
                    scrollToIndex(i);
                  }}
                  imageWidth={
                    theme.isMobile ? imageWidth ?? 0 : (imageWidth ?? 0) + 48
                  }
                  thumbColor={thumbColor}
                  thumbSize={thumbSize}
                  thumbResizeMode={thumbResizeMode}
                  thumbDefaultBorderColor={thumbDefaultBorderColor}
                  iconSize={iconSize ?? 0}
                  style={{
                    width: "calc(100% + 32px)",
                  }}
                />
              </ThumbContainer>
            )}
          </HeadChildContainer>
        </HeadContainer>
      </MainContainer>

      <LoaderModel
        isOpen={showLoaderModal}
        onClose={() => setLoaderModal(false)}
      />

      <GalleryViewModal
        isOpen={isSelectLanguageModalOpen}
        ReceivedMedia={images}
        activeIndex={activeIndex}
        setActiveIndex={scrollToIndex}
        onClose={handleModalClose}
        image_thumb_url={itemDetail?.image_thumb_url}
        productType={productType}
        itemDetail={itemDetail}
        isGuest={isGuest}
        fromReviewPage={fromReviewPage}
        isJewelryComponent={isJewelryComponent}
        fromSearchResult={false}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setshowShareModal(false)}
        item={
          isMatchModel
            ? itemDetail
            : shareDiamond
            ? stoneItemDetail?.type !== ProductCategory.JEWELRY &&
              stoneItemDetail?.type !== ProductCategory.LABGROWN_JEWELRY
              ? stoneItemDetail
              : shareItemDetail
            : fromReviewPage
            ? stoneItemDetail?.type === ProductCategory.JEWELRY ||
              stoneItemDetail?.type === ProductCategory.LABGROWN_JEWELRY
              ? stoneItemDetail
              : shareItemDetail
            : shareItemDetail
        }
        jcSelectedOption={jcSelectedOption}
        personalization={mapKeysToLabels(
          personalization,
          personalizationSectionLayout["sub-sections"]
        )}
        {...(fromReviewPage &&
          !isModal && {
            stoneItemDetail:
              stoneItemDetail?.type === ProductCategory.JEWELRY ||
              stoneItemDetail?.type === ProductCategory.LABGROWN_JEWELRY
                ? shareItemDetail
                : stoneItemDetail,
            title: t("reviewPage.share.screentext.header.text", {
              x: (itemDetail as any)?.jewelry_type,
            }),
          })}
        {...(Object.keys(matchingPairItemDetail || {}) && {
          matchingPairItemDetail: matchingPairItemDetail,
        })}
        shareType={ShareType.singleShare}
        selectedOption={selectedOption as ShareVia}
        fromReviewPage={fromReviewPage && !isModal}
      />

      {images && resizeImage && theme.isMobile ? (
        <FooterContainer resizeImage={resizeImage}>
          {renderFooterComponent?.(images[activeIndex], activeIndex)}
        </FooterContainer>
      ) : null}
    </Container>
  );
};

const HeadContainer = styled.View<any>`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? "row-reverse" : "column"};
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    (!theme.isMobile && Platform.OS !== "web") || theme.isTablet
      ? theme["core-01"]
      : undefined};
  border-radius: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile ? 16 : 0}px;
  padding-bottom: ${({ isEmpty }: { isEmpty: boolean }) =>
    isEmpty ? `${SpaceType.Double}px` : 0};
  gap: ${({ isEmpty }: { isEmpty: boolean }) =>
    isEmpty ? `${SpaceType.Basic}px` : 0};
  width: ${({
    theme,
    resizeImage,
    deviceWidth,
  }: {
    theme: DefaultTheme;
    resizeImage: boolean;
    deviceWidth: number;
  }) =>
    resizeImage ? (theme.isLargeScreen ? deviceWidth : 380) : deviceWidth}px;
  ${({ theme }: { theme: DefaultTheme }) =>
    ((!theme.isMobile && Platform.OS !== "web") || theme.isTablet) &&
    css`
      background-color: ${theme["core-01"]};
    `}
`;

const HeadChildContainer = styled.View`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    Platform.OS === "web" && theme.isLargeScreen ? "column-reverse" : "column"};
  border-radius: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile ? 16 : 0}px;
  justify-content: flex-end;
`;

const NewHeadContainer = styled.View`
  flex-direction: row;
  align-items: center;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;

const IndexContainer = styled.View<{ imageHeight: number }>`
  margin-bottom: 0px;
  border-radius: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? 24 : 0}px;
`;

const FooterContainer = styled.View<{ resizeImage: boolean }>`
  margin-bottom: ${({ resizeImage }: { resizeImage: boolean }) =>
    resizeImage ? "10px" : "10px"};
  margin-left: ${({ resizeImage }: { resizeImage: boolean }) =>
    resizeImage ? "10px" : "0px"};
  margin-right: ${({ resizeImage }: { resizeImage: boolean }) =>
    resizeImage ? "10px" : "0px"};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const ThumbContainer = styled.View<any>`
  flex-direction: row;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  padding: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen
      ? `${SpaceType.Basic}px 0 ${SpaceType.Basic}px ${SpaceType.Basic}px`
      : 0};
  width: ${({
    theme,
    resizeImage,
    deviceWidth,
  }: {
    theme: DefaultTheme;
    resizeImage: boolean;
    deviceWidth: number;
  }) =>
    resizeImage ? (theme.isLargeScreen ? deviceWidth : 380) : deviceWidth}px;
`;

const Container = styled.View``;

const MainContainer = styled.View<{ isSmallDeviceWidth: boolean }>`
  ${({
    isSmallDeviceWidth,
    theme,
  }: {
    isSmallDeviceWidth: boolean;
    theme: DefaultTheme;
  }) =>
    !isSmallDeviceWidth &&
    css`
      background-color: ${theme["core-01"]};
    `};
`;

const HeaderTextContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const IconContainer = styled.View<any>`
  padding: ${SpaceType.Basic}px;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const ImageContainer = styled.View<any>`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  height: ${({
    screenWidth,
    theme,
  }: {
    screenWidth: number;
    theme: DefaultTheme;
  }) =>
    theme.isMobile && screenWidth
      ? screenWidth + "px"
      : (screenWidth as number)
      ? screenWidth + "px"
      : "100%"};
  width: ${({ screenWidth }: { screenWidth: number }) =>
    `${screenWidth ? screenWidth + "px" : "100%"}`};
  overflow: hidden;
`;

const PdpMediaActionsContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  z-index: 2;
`;

export default ImageGallery;
